<script setup lang="ts">
import { ref, computed } from 'vue';
import { RouterView, RouterLink, useRoute, useRouter } from 'vue-router';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
  useTenantStore,
  useAuthStore,
  useAppointmentsStore,
  useInvoicesStore,
  useQuotesStore,
} from '@sera/core';
import Drawer from 'primevue/drawer';
import {
  faBars,
  faChevronRight,
  faCalendarPlus,
  faHouse,
  faCalendarDays,
  faFileInvoiceDollar,
  faReceipt,
  faBug,
  faPhone,
} from '@fortawesome/pro-solid-svg-icons';
import AppFooter from '../components/AppFooter.vue';
import TenantLogo from '../components/TenantLogo.vue';
import SidebarNavItem from '../components/SidebarNavItem.vue';
import SidebarProfile from '../components/SidebarProfile.vue';

const tenantStore = useTenantStore();
const authStore = useAuthStore();
const appointmentsStore = useAppointmentsStore();
const invoicesStore = useInvoicesStore();
const quotesStore = useQuotesStore();
const route = useRoute();
const router = useRouter();

// Format integer cents to USD for the invoice badge tooltip.
const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});
function formatCents(cents: number): string {
  return currencyFormatter.format(cents / 100);
}

// Per-nav-item badge props. Keyed by navItems[].name; render-site does a
// lookup. Counts of 0 cause NavBadge to render nothing, so the conditional
// hide-when-empty rule lives there, not here.
interface NavBadgeProps {
  count: number;
  variant: 'primary' | 'pending' | 'info';
  dot: boolean;
  tooltip?: string;
}

const navBadges = computed<Record<string, NavBadgeProps | undefined>>(() => ({
  appointments: {
    count: appointmentsStore.actionableCount,
    variant: 'info',
    dot: false,
  },
  // D-06: list-consistent count (all report rows). Amber 'pending' variant.
  quotes: {
    count: quotesStore.badgeCount,
    variant: 'pending',
    dot: false,
  },
  invoices: {
    count: invoicesStore.unpaidCount,
    variant: 'primary',
    dot: invoicesStore.hasPastDue,
    tooltip:
      invoicesStore.unpaidCount > 0
        ? `${invoicesStore.unpaidCount} unpaid · ${formatCents(invoicesStore.unpaidTotalCents)} due`
        : undefined,
  },
}));

// D-03: Home, Appointments, Quotes, Invoices; Dev Mode in dev builds only.
// (Settings removed in 6.2 D-08 — theme moved to /profile?tab=preferences.)
// Quotes is a top-level sibling (NOT nested under Invoices): it is a
// pre-service funnel stage that precedes Invoices, so it sits between
// Appointments and Invoices. Adding it here makes the BILL-14 /quotes surface
// reachable from the nav. See .planning/research/phase-6/ia-gaps.md #6.
const navItems = [
  { name: 'home', label: 'Home', to: '/', icon: faHouse },
  {
    name: 'appointments',
    label: 'Appointments',
    to: '/appointments',
    icon: faCalendarDays,
  },
  {
    name: 'quotes',
    label: 'Quotes',
    to: '/quotes',
    icon: faFileInvoiceDollar,
  },
  { name: 'invoices', label: 'Invoices', to: '/invoices', icon: faReceipt },
  ...(import.meta.env.DEV
    ? [{ name: 'dev', label: 'Dev Mode', to: '/dev', icon: faBug }]
    : []),
];

const sidebarCollapsed = ref(false);
const drawerOpen = ref(false);

const isBranded = computed(() => !!tenantStore.config?.logoUrl);
const isOnSchedule = computed(() => route.path.startsWith('/schedule'));

// PODZ-1835 (updated): when the tenant disables customer scheduling, the header
// CTAs are replaced by an inline "Call to schedule" block linking to the tenant
// phone (tel:) — no call-office modal. The block hides entirely when no phone
// is configured. Phone comes from the already-loaded tenant config.
const isSchedulingAllowed = computed(() => tenantStore.isSchedulingAllowed);
const tenantPhone = computed(() => tenantStore.footerContact.phone);

const displayName = computed(() => {
  const u = authStore.user;
  return u ? `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() : '';
});

const initials = computed(() => {
  const u = authStore.user;
  return u
    ? (
        (u.first_name?.charAt(0) ?? '') + (u.last_name?.charAt(0) ?? '')
      ).toUpperCase()
    : '';
});

function toggleSidebar(): void {
  sidebarCollapsed.value = !sidebarCollapsed.value;
}

function isActive(to: string): boolean {
  if (to === '/') return route.path === '/';
  if (route.path.startsWith(to)) return true;
  // Appointment detail / summary routes live under the Appointments nav item
  // but use singular paths (/appointment/:id, /appointment-summary/:id) that
  // do not prefix-match /appointments — keep the parent nav item highlighted
  // when the user drills into a card.
  if (to === '/appointments') {
    return (
      route.path.startsWith('/appointment/') ||
      route.path.startsWith('/appointment-summary/')
    );
  }
  return false;
}

async function handleLogout(): Promise<void> {
  drawerOpen.value = false;
  await authStore.logout();
  await router.push('/login');
}
</script>

<template>
  <div class="h-dvh overflow-hidden bg-canvas flex flex-col">
    <!-- ── MOBILE TOP BAR (hidden at md+) ──────────────────────────
         Secondary-color background on the mobile bar (var(--color-secondary) direct). -->
    <header
      data-testid="mobile-top-bar"
      class="md:hidden flex items-center gap-[10px] bg-secondary h-(--nav-height) px-4 shrink-0"
    >
      <!-- Hamburger LEFT -->
      <button
        data-testid="hamburger-btn"
        type="button"
        aria-label="Open navigation"
        class="size-10 shrink-0 flex items-center justify-center rounded-md text-white hover:bg-white/10"
        @click="drawerOpen = true"
      >
        <FontAwesomeIcon :icon="faBars" class="size-5" />
      </button>
      <!-- Logo centre (grows) -->
      <RouterLink to="/" class="flex items-center flex-1 min-w-0">
        <TenantLogo :max-height="36" :max-width="110" fallback="text" />
      </RouterLink>
      <!-- Schedule RIGHT (hidden when already on /schedule). PODZ-1835: link to
           the wizard when scheduling is allowed, else an inline "Call to
           schedule" block (tel: link, native dialer on mobile). The block hides
           when no tenant phone is configured. -->
      <RouterLink
        v-if="!isOnSchedule && isSchedulingAllowed"
        to="/schedule"
        data-testid="schedule-cta-mobile"
        class="shrink-0 inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-primary text-white text-sm font-semibold hover:bg-[color-mix(in_srgb,var(--color-primary)_88%,black)] transition-colors"
      >
        <FontAwesomeIcon :icon="faCalendarPlus" class="size-3.5" />
        <span class="hidden sm:inline">Schedule appointment</span>
        <span class="sm:hidden">Schedule</span>
      </RouterLink>
      <div
        v-else-if="!isOnSchedule && tenantPhone"
        data-testid="schedule-cta-mobile"
        class="shrink-0 flex flex-col items-end leading-tight"
      >
        <span class="text-white/80 text-xs">Call to schedule</span>
        <a
          :href="`tel:${tenantPhone}`"
          class="flex items-center gap-2 text-white text-sm hover:underline"
        >
          <FontAwesomeIcon :icon="faPhone" class="size-4" />
          <span>{{ tenantPhone }}</span>
        </a>
      </div>
    </header>

    <!-- ── DESKTOP HEADER ROW (hidden on mobile) ───────────────────
         shell-hd: brand-cell (220px fixed) + topbar (flex-1) -->
    <div
      class="hidden md:flex flex-row shrink-0 h-(--nav-height) transition-colors duration-200"
      :class="
        isBranded ? 'border-b border-transparent' : 'border-b border-border'
      "
    >
      <!-- Brand cell — fixed 220px, never collapses -->
      <div
        data-testid="appshell-brand-cell"
        class="w-[220px] flex items-center px-4 shrink-0 transition-colors duration-200"
        :class="
          isBranded
            ? 'bg-secondary border-r border-transparent'
            : 'bg-surface border-r border-border'
        "
      >
        <RouterLink
          to="/"
          class="flex items-center gap-0 min-w-0 overflow-hidden"
        >
          <TenantLogo :max-height="36" :max-width="160" fallback="wordmark" />
        </RouterLink>
      </div>

      <!-- Topbar — Schedule CTA -->
      <div
        class="flex-1 flex items-center justify-end px-6 transition-colors duration-200"
        :class="isBranded ? 'bg-secondary' : 'bg-surface'"
      >
        <RouterLink
          v-if="!isOnSchedule && isSchedulingAllowed"
          to="/schedule"
          data-testid="schedule-cta-desktop"
          class="inline-flex items-center gap-2 h-9 px-4 rounded-md bg-primary text-white text-sm font-semibold hover:bg-[color-mix(in_srgb,var(--color-primary)_88%,black)] transition-colors"
        >
          <FontAwesomeIcon :icon="faCalendarPlus" class="size-4" />
          Schedule appointment
        </RouterLink>
        <div
          v-else-if="!isOnSchedule && tenantPhone"
          data-testid="schedule-cta-desktop"
          class="flex flex-col items-end leading-tight"
        >
          <span class="text-white/80 text-xs">Call to schedule</span>
          <a
            :href="`tel:${tenantPhone}`"
            class="flex items-center gap-2 text-white text-sm hover:underline"
          >
            <FontAwesomeIcon :icon="faPhone" class="size-4" />
            <span>{{ tenantPhone }}</span>
          </a>
        </div>
      </div>
    </div>

    <!-- ── BODY ROW: sidebar (desktop only) + single main ──────────
         md:overflow-hidden constrains desktop layout; mobile page scrolls naturally. -->
    <div class="flex flex-row flex-1 min-h-0 md:overflow-hidden">
      <!-- Left sidebar — desktop only, collapsible -->
      <aside
        data-testid="sidebar"
        class="hidden md:flex flex-col shrink-0 bg-surface border-r border-border transition-[width] duration-200 ease-in-out overflow-hidden"
        :class="sidebarCollapsed ? 'w-[60px]' : 'w-[220px]'"
      >
        <!-- Sidebar collapse control -->
        <button
          data-testid="sidebar-toggle"
          type="button"
          :aria-label="
            sidebarCollapsed ? 'Expand navigation' : 'Collapse navigation'
          "
          class="flex items-center h-11 w-full border-b border-border bg-transparent text-muted hover:bg-canvas hover:text-foreground transition-[background,color] duration-120 cursor-pointer shrink-0"
          @click="toggleSidebar"
        >
          <span
            class="shrink-0 w-[53px] flex items-center justify-center text-xs"
          >
            <FontAwesomeIcon
              :icon="faChevronRight"
              class="size-3 transition-transform duration-200"
              :class="sidebarCollapsed ? '' : 'rotate-180'"
            />
          </span>
          <span
            class="text-xs font-medium overflow-hidden whitespace-nowrap transition-[max-width,opacity] duration-200"
            :class="
              sidebarCollapsed
                ? 'max-w-0 opacity-0'
                : 'max-w-[160px] opacity-100'
            "
            >Collapse sidebar</span
          >
        </button>

        <!-- Nav items — accent-bar active pattern -->
        <nav class="flex-1 overflow-hidden py-1">
          <SidebarNavItem
            v-for="item in navItems"
            :key="item.name"
            :to="item.to"
            :label="item.label"
            :icon="item.icon"
            :active="isActive(item.to)"
            :collapsed="sidebarCollapsed"
            :badge-count="navBadges[item.name]?.count ?? 0"
            :badge-variant="navBadges[item.name]?.variant ?? 'primary'"
            :badge-dot="navBadges[item.name]?.dot ?? false"
            :badge-tooltip="navBadges[item.name]?.tooltip"
            item-height="h-11"
          />
        </nav>

        <!-- Profile section at sidebar bottom -->
        <SidebarProfile
          :display-name="displayName"
          :initials="initials"
          :profile-active="isActive('/profile')"
          :collapsed="sidebarCollapsed"
          avatar-size="sm"
          item-height="h-11"
          @logout="handleLogout"
        />

        <AppFooter v-show="!sidebarCollapsed" class="shrink-0" />
      </aside>

      <!-- Single main for all viewports. main is the scroll container at
           every breakpoint because main.css pins html/body to height:100%
           overflow:hidden (desktop wizard-scroll fix). Both min-h-0 and
           overflow-y-auto are required: min-h-0 lets the flex item shrink
           below intrinsic content size so overflow-y-auto actually scrolls
           instead of growing the box past the viewport. -->
      <main class="flex-1 flex flex-col min-w-0 min-h-0 overflow-y-auto">
        <RouterView v-slot="{ Component }">
          <Transition name="page-fade" mode="out-in">
            <component :is="Component" />
          </Transition>
        </RouterView>
        <AppFooter class="md:hidden mt-auto shrink-0" />
      </main>
    </div>

    <!-- ── MOBILE DRAWER (PrimeVue) ──────────────────────────────
         Uses PrimeVue <Drawer position="left"> for slide animation + focus trap.
         Only opened from the mobile top bar hamburger (md:hidden). -->
    <Drawer
      v-model:visible="drawerOpen"
      position="left"
      aria-label="Navigation"
      data-testid="mobile-drawer"
      :pt="{
        root: 'w-72 h-full bg-surface shadow-lg flex flex-col p-0 overflow-hidden',
        mask: 'bg-black/50',
        header:
          'flex items-center justify-between px-4 shrink-0 bg-secondary border-b border-border h-(--nav-height) p-0',
        title: 'hidden',
        content: 'flex flex-col flex-1 overflow-hidden p-0',
        pcCloseButton: {
          root: 'size-9 inline-flex items-center justify-center rounded-md text-white hover:bg-white/10 cursor-pointer',
        },
      }"
    >
      <template #header>
        <RouterLink
          v-if="tenantStore.config?.logoUrl"
          to="/"
          class="flex items-center"
          @click="drawerOpen = false"
        >
          <TenantLogo :max-height="30" :max-width="110" fallback="none" />
        </RouterLink>
      </template>

      <!-- Nav links — accent-bar pattern, 48px height (thumb-friendly) -->
      <nav class="flex-1 overflow-y-auto py-1">
        <SidebarNavItem
          v-for="item in navItems"
          :key="item.name"
          :to="item.to"
          :label="item.label"
          :icon="item.icon"
          :active="isActive(item.to)"
          :badge-count="navBadges[item.name]?.count ?? 0"
          :badge-variant="navBadges[item.name]?.variant ?? 'primary'"
          :badge-dot="navBadges[item.name]?.dot ?? false"
          :badge-tooltip="navBadges[item.name]?.tooltip"
          item-height="h-12"
          @click="drawerOpen = false"
        />
      </nav>

      <!-- Profile section at drawer bottom -->
      <SidebarProfile
        data-testid="drawer-profile"
        :display-name="displayName"
        :initials="initials"
        :profile-active="isActive('/profile')"
        @logout="handleLogout"
        @navigate="drawerOpen = false"
      />
    </Drawer>
  </div>
</template>
